/**
 * AppShell — layout wrapper for every LOGGED-IN page.
 *
 * Combines:
 *   - LeftRail (persistent on desktop, slide-over drawer on mobile)
 *   - InPageTopBar (slim, page-level affordances only)
 *   - A content area that fills the remaining viewport
 *
 * Public marketing pages (/, /pricing, /skills, etc.) do NOT use the
 * shell — they keep their own header. Use of the shell is opt-in at
 * the route level in App.js via the `requireShell` wrapper.
 *
 * Convention: a page passes its `title`, optional `subtitle`, and
 * optional `actions` via the WithShell wrapper. The page body should
 * NOT include its own UserMenu / global header — those have moved
 * into the rail.
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import LeftRail, { persistRailCollapsed, useRailCollapsed } from "./LeftRail";
import InPageTopBar from "./InPageTopBar";
import CommunityPickupCelebrator from "./CommunityPickupCelebrator";

export const AppShell = ({ title, subtitle, actions, children }) => {
  const { user, loading } = useAuth();
  const initialCollapsed = useRailCollapsed();
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close the mobile drawer on every route change so navigating
  // doesn't strand the overlay.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search, location.hash]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [mobileOpen]);

  // Listen for the Esc handler in LeftRail to close the mobile drawer.
  useEffect(() => {
    const close = () => setMobileOpen(false);
    document.addEventListener("ae:rail:close-mobile", close);
    return () => document.removeEventListener("ae:rail:close-mobile", close);
  }, []);

  const toggleCollapse = () => {
    setCollapsed((c) => {
      const next = !c;
      persistRailCollapsed(next);
      return next;
    });
  };

  // While auth is loading, render a blank shell to avoid a flash of
  // unstyled / mis-redirected content. Once the user resolves the
  // page itself decides whether to show its own login affordance.
  if (loading) {
    return <div className="min-h-screen bg-[#f4f5f7]" data-testid="app-shell-loading" />;
  }
  // Not signed in — render the page bare. The page is responsible
  // for showing its own AuthDialog / redirect to /. This keeps the
  // shell from forcing a rail on a guest who's just hit a deep link.
  if (!user) {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-screen bg-[#f4f5f7]" data-testid="app-shell">
      {/* Desktop rail */}
      <div className="hidden md:block">
        <LeftRail collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </div>

      {/* Mobile rail drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex h-[100dvh]" data-testid="app-shell-mobile-drawer">
          <button
            type="button"
            className="absolute inset-0 bg-black/45"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
            data-testid="app-shell-mobile-scrim"
          />
          <div className="relative h-full">
            <LeftRail collapsed={false} onToggleCollapse={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <InPageTopBar
          title={title}
          subtitle={subtitle}
          actions={actions}
          onOpenMobileRail={() => setMobileOpen(true)}
        />
        <main className="flex-1 min-w-0">{children}</main>
      </div>
      <CommunityPickupCelebrator />
    </div>
  );
};

/**
 * Convenience HOC — `withShell(PageComponent, { title })` returns a
 * component that renders PageComponent inside an AppShell. Use this
 * in App.js when the title is static; pages with dynamic titles
 * should render `<AppShell title={...}>...</AppShell>` directly.
 */
export const withShell = (PageComponent, shellProps = {}) => {
  const Wrapped = (props) => (
    <AppShell {...shellProps}>
      <PageComponent {...props} />
    </AppShell>
  );
  Wrapped.displayName = `WithShell(${PageComponent.displayName || PageComponent.name || "Page"})`;
  return Wrapped;
};

export default AppShell;
